import { useQuery } from '@apollo/client'
import { ColorPicker } from 'react-aria-components'
import { useParams } from 'react-router-dom'
import { GET_FACILITY_CLIMATE_SETTINGS_QUERY } from '~/client/queries'
import Panel, { PanelSection } from '~/components/UIKit/Panel'
import { PrimaryUses, primaryUses } from '~/config/facility'
import ObjectClassName from '~/config/objectClassNames'
import theme from '~/config/theme'
import { createAppSelector, useAppDispatch, useAppSelector } from '~/store/hooks'
import { SELECTED_COMFORT_ZONE_PANEL } from '~/store/panel/types'
import { mostRecentSelectedObjectOfClassName } from '~/store/selectedObjects/selectors'
import { ChooserWell } from '~/ui/Color'
import { DimensionInput } from '~/ui/DimensionInput'
import { Label } from '~/ui/Field'
import { NumberField } from '~/ui/NumberField'
import { Select, SelectItem } from '~/ui/Select'
import { Tab, TabList, TabPanel, Tabs } from '~/ui/Tabs'
import { TextField } from '~/ui/TextField'
import BerkeleyData from './BerkeleyData'
import { getPositionDiffs } from '~/components/DrawingCanvas/ComfortZones/utils'
import { useCallback } from 'react'
import { Matrix4, Vector3 } from 'three'
import { updateComfortZone } from '~/store/objects'
import { ComfortZone } from '~/store/objects/types'
import { Spinner } from '~/ui/Spinner'
import LayerKeys from '~/config/layerKeys'
import { imperialToMetric, metricToImperial } from '~/store/units/converters/temperature'

const selectIsTemperatureSimulation = createAppSelector([state => state.objects.present.products], stateProducts => {
  const products = Object.values(stateProducts)
  const isSummerTemperatureSimulation = products.some(({ layerKey }) => layerKey === LayerKeys.PRODUCTS_EVAP)
  const isWinterTemperatureSimulation = products.some(
    ({ layerKey, angle }) => layerKey === LayerKeys.PRODUCTS_HEATERS && angle !== undefined
  )
  return { isSummerTemperatureSimulation, isWinterTemperatureSimulation }
})

export function SelectedComfortZonePanel() {
  const isTouchUI = useAppSelector(({ userInterface }) => userInterface.isTouchUI)
  const selectedComfortZone = useAppSelector(store =>
    mostRecentSelectedObjectOfClassName(ObjectClassName.COMFORT_ZONE, store)
  )!
  const isLocked = useAppSelector(store => store.layers.layers.COMFORT_ZONES.locked)
  const { isSummerTemperatureSimulation, isWinterTemperatureSimulation } = useAppSelector(selectIsTemperatureSimulation)

  const params = useParams<{ id: string }>()
  const {
    data,
    loading: loadingFacility,
    error,
  } = useQuery(GET_FACILITY_CLIMATE_SETTINGS_QUERY, { variables: { id: params.id ?? '' }, skip: !params.id })

  const dispatch = useAppDispatch()

  const { xDiff: width, yDiff: length, xMin, xMax, yMin, yMax } = getPositionDiffs(selectedComfortZone.positions)

  const rescale = useCallback(
    (newWidth: number, newLength: number) => {
      const cx = (xMin + xMax) / 2
      const cy = (yMin + yMax) / 2

      const mtx = new Matrix4()
        .multiply(new Matrix4().makeTranslation(cx, cy, 0))
        .multiply(new Matrix4().makeScale(newWidth / width, newLength / length, 0))
        .multiply(new Matrix4().makeTranslation(-cx, -cy, 0))

      const newPosition = new Vector3().copy(selectedComfortZone.position).applyMatrix4(mtx)
      const newPositions = selectedComfortZone.positions.map(it => new Vector3().copy(it).applyMatrix4(mtx))

      dispatch(
        updateComfortZone({
          comfortZone: {
            id: selectedComfortZone.id,
            position: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
            positions: newPositions.map(pt => ({ x: pt.x, y: pt.y, z: pt.z })),
          },
        })
      )
    },
    [width, length, dispatch, xMin, xMax, yMin, yMax, selectedComfortZone]
  )

  const setName = useCallback(
    (name: string) => {
      dispatch(updateComfortZone({ comfortZone: { id: selectedComfortZone.id, name } }))
    },
    [dispatch, selectedComfortZone]
  )

  const setPrimaryUse = useCallback(
    (primaryUse: PrimaryUses) => {
      dispatch(
        updateComfortZone({
          comfortZone: {
            id: selectedComfortZone.id,
            primaryUse,
          },
        })
      )
    },
    [dispatch, selectedComfortZone]
  )

  const setColor = useCallback(
    (color: string) => {
      dispatch(
        updateComfortZone({
          comfortZone: {
            id: selectedComfortZone.id,
            color,
          },
        })
      )
    },
    [dispatch, selectedComfortZone]
  )

  const setHumidity = useCallback(
    (humidity: number, key: keyof Pick<ComfortZone, 'indoorWinterHumidity' | 'indoorSummerHumidity'>) => {
      dispatch(
        updateComfortZone({
          comfortZone: {
            id: selectedComfortZone.id,
            [key]: Math.round(humidity * 100),
          },
        })
      )
    },
    [dispatch, selectedComfortZone]
  )

  const setTemperature = useCallback(
    (temperature: number, key: keyof Pick<ComfortZone, 'indoorSummerTemp' | 'indoorWinterTemp'>) => {
      dispatch(
        updateComfortZone({
          comfortZone: {
            id: selectedComfortZone.id,
            [key]: temperature,
          },
        })
      )
    },
    [dispatch, selectedComfortZone]
  )

  if (loadingFacility) {
    return (
      <Panel
        title="Comfort Zone"
        alignment="right"
        docked
        panelKey={SELECTED_COMFORT_ZONE_PANEL}
        hasToolbar={isTouchUI}
      >
        <PanelSection>
          <div className="text-center">
            <Spinner />
          </div>
        </PanelSection>
      </Panel>
    )
  } else if (error || !data?.facility) {
    return (
      <Panel
        title="Comfort Zone"
        alignment="right"
        docked
        panelKey={SELECTED_COMFORT_ZONE_PANEL}
        hasToolbar={isTouchUI}
      >
        <PanelSection>
          <div className="text-center">
            <h3>Facility Data Failed To Load</h3>
            {error?.message}
          </div>
        </PanelSection>
      </Panel>
    )
  }

  const isMetric = data.facility.units === 'METRIC'
  const unit = isMetric ? 'celsius' : 'fahrenheit'

  const summerTemp = selectedComfortZone.indoorSummerTemp ?? data.facility?.indoorSummerTemp ?? 0
  const handleSaveSummerTemp = (value: number) => {
    setTemperature(isMetric ? metricToImperial(value) : value, 'indoorSummerTemp')
  }

  const winterTemp = selectedComfortZone.indoorWinterTemp ?? data.facility?.indoorWinterTemp ?? 0
  const handleSaveWinterTemp = (value: number) => {
    setTemperature(isMetric ? metricToImperial(value) : value, 'indoorWinterTemp')
  }

  return (
    <Panel
      scrollable
      title="Comfort Zone"
      alignment="right"
      docked
      panelKey={SELECTED_COMFORT_ZONE_PANEL}
      hasToolbar={isTouchUI}
    >
      <div className="flex flex-col h-full pb-10 overflow-y-auto">
        <PanelSection>
          <div className="flex flex-col items-start gap-3">
            <ColorPicker value={selectedComfortZone.color} onChange={color => setColor(color.toString('hex'))}>
              <Label>Color</Label>
              <ChooserWell swatches={theme.colors.swatches} isDisabled={isLocked} />
            </ColorPicker>
            <TextField label="Name" value={selectedComfortZone.name} onChange={setName} />
          </div>
        </PanelSection>
        <Tabs className="gap-0">
          <TabList className="p-4 py-0">
            <Tab id="data">Data</Tab>
            <Tab id="settings">Settings</Tab>
          </TabList>
          <TabPanel id="data" className="p-0">
            <BerkeleyData
              comfortZone={selectedComfortZone}
              primaryUse={selectedComfortZone.primaryUse ?? data.facility?.primaryUse ?? 'OTHER'}
              primaryType={data.facility?.primaryType ?? 'INDUSTRIAL'}
              airTemp={selectedComfortZone.indoorSummerTemp ?? data.facility?.indoorSummerTemp}
              humidity={selectedComfortZone.indoorSummerHumidity ?? data.facility?.indoorHumidity}
            />
          </TabPanel>
          <TabPanel id="settings" className="flex flex-col gap-3">
            <DimensionInput
              label="Width"
              value={width}
              onCommit={newWidth => {
                rescale(newWidth, length)
              }}
            />
            <DimensionInput
              label="Length"
              value={length}
              onCommit={newLength => {
                rescale(width, newLength)
              }}
            />
            <Select
              name="primaryUse"
              label="Primary Use"
              selectedKey={selectedComfortZone.primaryUse}
              onSelectionChange={key => setPrimaryUse(key as PrimaryUses)}
            >
              {primaryUses.map(({ value, label }) => (
                <SelectItem id={value} key={value}>
                  {label}
                </SelectItem>
              ))}
            </Select>
            <NumberField
              label="Indoor Summer Temp"
              isDisabled={isSummerTemperatureSimulation}
              value={isMetric ? imperialToMetric(summerTemp) : summerTemp}
              formatOptions={{ style: 'unit', unit }}
              onChange={handleSaveSummerTemp}
              isInvalid={isSummerTemperatureSimulation}
              errorMessage='If using evaporative coolers, adjust "Advanced Climate" settings when requesting the CFD.'
            />
            <NumberField
              label="Indoor Winter Temp"
              isDisabled={isWinterTemperatureSimulation}
              value={isMetric ? imperialToMetric(winterTemp) : winterTemp}
              formatOptions={{ style: 'unit', unit }}
              onChange={handleSaveWinterTemp}
              isInvalid={isWinterTemperatureSimulation}
              errorMessage='If using heaters, adjust "Advanced Climate" settings when requesting the CFD.'
            />
            <NumberField
              label="Summer Humidity"
              value={(selectedComfortZone.indoorSummerHumidity ?? data?.facility?.indoorHumidity ?? 0) / 100}
              formatOptions={{ style: 'percent' }}
              onChange={value => setHumidity(value, 'indoorSummerHumidity')}
            />
            <NumberField
              label="Winter Humidity"
              value={(selectedComfortZone.indoorWinterHumidity ?? data?.facility?.indoorWinterHumidity ?? 0) / 100}
              formatOptions={{ style: 'percent' }}
              onChange={value => setHumidity(value, 'indoorWinterHumidity')}
            />
          </TabPanel>
        </Tabs>
      </div>
    </Panel>
  )
}
